// src/components/ContactFormSection.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Snackbar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { submitContactInquiry } from '../services/apiService';

const ContactFormSection = ({ 
  title,
  subtitle,
  source = "unknown",
  backgroundColor = '#e8f0f7',
  containerMaxWidth = 'md'
}) => {
  const { t, i18n } = useTranslation('landing');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  // Use provided title/subtitle or fallback to translations
  const displayTitle = title || t('contact.form.title');
  const displaySubtitle = subtitle || t('contact.form.subtitle');
  
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    industry: '',
    inquiryType: t('contact.form.inquiryTypes.asap')
  });

  const [formErrors, setFormErrors] = useState({});

  // Get translated industries
  const industries = t('contact.form.industries', { returnObjects: true });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.companyName.trim()) {
      errors.companyName = t('contact.form.errors.companyRequired');
    }
    
    if (!formData.contactName.trim()) {
      errors.contactName = t('contact.form.errors.nameRequired');
    }
    
    if (!formData.phone.trim()) {
      errors.phone = t('contact.form.errors.phoneRequired');
    } else if (!/^[0-9-+()#\s]+$/.test(formData.phone)) {
      errors.phone = t('contact.form.errors.phoneInvalid');
    }
    
    if (!formData.email.trim()) {
      errors.email = t('contact.form.errors.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('contact.form.errors.emailInvalid');
    }
    
    if (!formData.industry) {
      errors.industry = t('contact.form.errors.industryRequired');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Add inquiry type to message
      let message = '';
      if (formData.inquiryType) {
        message = `${t('contact.form.timeline')}: ${formData.inquiryType}`;
      }
      
      // Prepare form data with message
      const submitData = {
        ...formData,
        message: message || null
      };
      
      // Use submitContactInquiry from apiService
      const response = await submitContactInquiry(submitData);
      
      if (response.data.success) {
        setSuccess(true);
        setSnackbarOpen(true);
        // Reset form
        setFormData({
          companyName: '',
          contactName: '',
          phone: '',
          email: '',
          industry: '',
          inquiryType: t('contact.form.inquiryTypes.asap')
        });
        
        // Auto close success message after 5 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.message || t('contact.form.submitError'));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ 
      py: 8, 
      backgroundColor: backgroundColor,
      minHeight: '600px'
    }}>
      <Container maxWidth={containerMaxWidth}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            backgroundColor: 'white'
          }}
        >
          {success ? (
            <Box textAlign="center" py={8}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {t('contact.form.successTitle')}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {t('contact.form.successMessage')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box textAlign="center" mb={4}>
                <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" sx={{ color: '#212121' }}>
                  {displayTitle}
                </Typography>
                <Typography variant="body1" sx={{ color: '#424242' }}>
                  {displaySubtitle}
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('contact.form.fields.company')}
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      error={!!formErrors.companyName}
                      helperText={formErrors.companyName}
                      required
                      placeholder={t('contact.form.placeholders.company')}
                      InputLabelProps={{
                        sx: { color: '#424242', '&.Mui-focused': { color: '#1976d2' } }
                      }}
                      InputProps={{
                        sx: { color: '#212121' }
                      }}
                      sx={{
                        '& .MuiFormHelperText-root': { color: '#616161' }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('contact.form.fields.name')}
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      error={!!formErrors.contactName}
                      helperText={formErrors.contactName}
                      required
                      placeholder={t('contact.form.placeholders.name')}
                      InputLabelProps={{
                        sx: { color: '#424242', '&.Mui-focused': { color: '#1976d2' } }
                      }}
                      InputProps={{
                        sx: { color: '#212121' }
                      }}
                      sx={{
                        '& .MuiFormHelperText-root': { color: '#616161' }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('contact.form.fields.phone')}
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      error={!!formErrors.phone}
                      helperText={formErrors.phone || t('contact.form.phoneHelper')}
                      required
                      placeholder={t('contact.form.placeholders.phone')}
                      InputLabelProps={{
                        sx: { color: '#424242', '&.Mui-focused': { color: '#1976d2' } }
                      }}
                      InputProps={{
                        sx: { color: '#212121' }
                      }}
                      sx={{
                        '& .MuiFormHelperText-root': { color: '#616161' }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('contact.form.fields.email')}
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                      required
                      placeholder={t('contact.form.placeholders.email')}
                      InputLabelProps={{
                        sx: { color: '#424242', '&.Mui-focused': { color: '#1976d2' } }
                      }}
                      InputProps={{
                        sx: { color: '#212121' }
                      }}
                      sx={{
                        '& .MuiFormHelperText-root': { color: '#616161' }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label={t('contact.form.fields.industry')}
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      error={!!formErrors.industry}
                      helperText={formErrors.industry || t('contact.form.industryHelper')}
                      required
                      InputLabelProps={{
                        sx: { color: '#424242', '&.Mui-focused': { color: '#1976d2' } }
                      }}
                      InputProps={{
                        sx: { color: '#212121' }
                      }}
                      sx={{
                        '& .MuiFormHelperText-root': { color: '#616161' }
                      }}
                    >
                      <MenuItem value="">
                        <em>{t('contact.form.selectIndustry')}</em>
                      </MenuItem>
                      {Array.isArray(industries) && industries.map((industry) => (
                        <MenuItem key={industry} value={industry}>
                          {industry}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ mb: 1, color: '#424242', '&.Mui-focused': { color: '#1976d2' } }}>
                        {t('contact.form.inquiryQuestion')}
                      </FormLabel>
                      <RadioGroup
                        name="inquiryType"
                        value={formData.inquiryType}
                        onChange={handleChange}
                      >
                        <FormControlLabel 
                          value={t('contact.form.inquiryTypes.asap')} 
                          control={<Radio />} 
                          label={t('contact.form.inquiryTypes.asap')}
                          sx={{ '& .MuiFormControlLabel-label': { color: '#212121' } }}
                        />
                        <FormControlLabel 
                          value={t('contact.form.inquiryTypes.threeMonths')} 
                          control={<Radio />} 
                          label={t('contact.form.inquiryTypes.threeMonths')}
                          sx={{ '& .MuiFormControlLabel-label': { color: '#212121' } }}
                        />
                        <FormControlLabel 
                          value={t('contact.form.inquiryTypes.exploring')} 
                          control={<Radio />} 
                          label={t('contact.form.inquiryTypes.exploring')}
                          sx={{ '& .MuiFormControlLabel-label': { color: '#212121' } }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  
                  {error && (
                    <Grid item xs={12}>
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                      </Alert>
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          px: 6,
                          py: 1.5,
                          fontSize: '1.1rem',
                          borderRadius: 25,
                          backgroundColor: '#ff8c00',
                          '&:hover': {
                            backgroundColor: '#ff7700'
                          }
                        }}
                      >
                        {loading ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                            {t('contact.form.submitting')}
                          </>
                        ) : (
                          t('contact.form.submitButton')
                        )}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || t('contact.form.snackbarSuccess')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactFormSection;